/**
 * Community Audit Production Status Component
 *
 * Enhanced design displaying audit history with timerange information,
 * matching the visual style of other audit panels.
 */

import { BaseItem, Shimmer, Text } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import { ProgressBar } from '../../core/components';

import * as styles from './CommunityAuditProductionStatus.css';
import * as shimmerStyles from './shared/shimmer.css';

interface ComprehensiveAnalytics {
  lastAuditDate?: string | null;
  confidence?: string | null;
  totalReports: number;
  auditSessions: number;
  itemsAudited: number;
}

interface AuditSession {
  id: string;
  status: string;
  createdAt: string;
  completedAt?: string | null;
  sampleParameters?: {
    samplingParameters?: {
      timeframe?: {
        start: string;
        end: string;
      } | null;
    } | null;
  } | null;
}

interface CommunityAuditProductionStatusProps {
  comprehensiveAnalytics?: ComprehensiveAnalytics | null;
  auditSessions?: AuditSession[];
  isLoading?: boolean;
}

// Helper function to format date range
function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const formatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: startDate.getFullYear() !== endDate.getFullYear() ? 'numeric' : undefined,
  };

  return `${startDate.toLocaleDateString('en-US', formatOptions)} - ${endDate.toLocaleDateString('en-US', formatOptions)}`;
}

// Helper function to get the most recent completed audit session
function getLastAuditInfo(sessions?: AuditSession[]) {
  if (!sessions?.length) return null;

  const completedSessions = sessions
    .filter(session => session.completedAt)
    .sort((a, b) => {
      const dateA = b.completedAt ? new Date(b.completedAt).getTime() : 0;
      const dateB = a.completedAt ? new Date(a.completedAt).getTime() : 0;

      return dateA - dateB;
    });

  return completedSessions[0] || null;
}

// Helper function to get active sessions count
function getActiveSessionsCount(sessions?: AuditSession[]): number {
  if (!sessions?.length) return 0;

  return sessions.filter(session => session.status === 'active' || session.status === 'in_progress').length;
}

// Helper function to get confidence as percentage
function getConfidencePercentage(confidence?: string | null): number {
  if (!confidence) return 0;
  const match = confidence.match(/(\d+)/);

  return match ? parseInt(match[1], 10) : 0;
}

// eslint-disable-next-line complexity
export function CommunityAuditProductionStatus({ comprehensiveAnalytics, auditSessions = [], isLoading = false }: CommunityAuditProductionStatusProps) {
  const lastAuditInfo = useMemo(() => getLastAuditInfo(auditSessions), [auditSessions]);
  const activeSessionsCount = useMemo(() => getActiveSessionsCount(auditSessions), [auditSessions]);
  const confidencePercentage = useMemo(() => getConfidencePercentage(comprehensiveAnalytics?.confidence), [comprehensiveAnalytics?.confidence]);

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.panel}>
        <Text className={styles.title} size="base" weight="medium">
          <Shimmer className={shimmerStyles.shimmerHeader} />
        </Text>
        <BaseItem as="button" background="visible" className={styles.content} type="button">
          <div className={styles.twoColumnLayout}>
            <div className={styles.leftColumn}>
              <Shimmer className={shimmerStyles.shimmerSectionTitle} />
              {Array.from({ length: 2 }, (_, i) => `shimmer-left-${i}`).map(shimmerKey => (
                <div key={shimmerKey} className={shimmerStyles.shimmerItem}>
                  <Shimmer className={shimmerStyles.shimmerLabel} />
                  <Shimmer className={shimmerStyles.shimmerSmallValue} />
                </div>
              ))}
            </div>
            <div className={styles.rightColumn}>
              <Shimmer className={shimmerStyles.shimmerSectionTitle} />
              <div className={shimmerStyles.shimmerItem}>
                <Shimmer className={shimmerStyles.shimmerLabel} />
                <Shimmer className={shimmerStyles.shimmerSmallValue} />
              </div>
              <Shimmer className={shimmerStyles.shimmerProgressBar} />
            </div>
          </div>
          <div className={styles.bottomSection}>
            <Shimmer className={shimmerStyles.shimmerSectionTitle} />
            {Array.from({ length: 3 }, (_, i) => `shimmer-bottom-${i}`).map(shimmerKey => (
              <div key={shimmerKey} className={shimmerStyles.shimmerItem}>
                <Shimmer className={shimmerStyles.shimmerLabel} />
                <Shimmer className={shimmerStyles.shimmerSmallValue} />
              </div>
            ))}
          </div>
        </BaseItem>
      </div>
    );
  }

  // If no analytics data, show N/A state
  if (!comprehensiveAnalytics) {
    return (
      <div className={styles.panel}>
        <Text className={styles.title} size="base" weight="medium">
          Audit History
        </Text>
        <BaseItem as="button" background="visible" className={styles.content} type="button">
          <div className={styles.statusItem}>
            <Text color="secondary" size="sm">
              No data available
            </Text>
            <Text size="sm" weight="semibold">
              N/A
            </Text>
          </div>
        </BaseItem>
      </div>
    );
  }

  const analytics = comprehensiveAnalytics;

  return (
    <div className={styles.panel}>
      <Text className={styles.title} size="base" weight="medium">
        Audit History
      </Text>

      <BaseItem as="button" background="visible" className={styles.content} type="button">
        {/* Two-Column Layout */}
        <div className={styles.twoColumnLayout}>
          {/* Left Column - Last Audit */}
          <div className={styles.leftColumn}>
            <div className={styles.sectionHeader}>
              <Text color="secondary" size="xs" weight="medium">
                LAST AUDIT
              </Text>
            </div>

            <div className={styles.statusItem}>
              <Text className={styles.statusLabel} color="secondary" size="sm">
                Completed
              </Text>
              <Text className={styles.statusValue} size="sm" weight="semibold">
                {analytics.lastAuditDate || 'N/A'}
              </Text>
            </div>

            {lastAuditInfo?.sampleParameters?.samplingParameters?.timeframe && (
              <div className={styles.statusItem}>
                <Text className={styles.statusLabel} color="secondary" size="sm">
                  Period
                </Text>
                <Text className={styles.statusValue} size="sm" weight="semibold">
                  {formatDateRange(
                    lastAuditInfo.sampleParameters.samplingParameters.timeframe.start,
                    lastAuditInfo.sampleParameters.samplingParameters.timeframe.end
                  )}
                </Text>
              </div>
            )}
          </div>

          {/* Right Column - Confidence Level */}
          <div className={styles.rightColumn}>
            <div className={styles.sectionHeader}>
              <Text color="secondary" size="xs" weight="medium">
                CONFIDENCE LEVEL
              </Text>
            </div>

            {analytics.confidence ? (
              <div className={styles.progressItem}>
                <div className={styles.progressHeader}>
                  <Text className={styles.statusLabel} color="secondary" size="sm">
                    Current
                  </Text>
                  <Text className={styles.statusValue} size="sm" weight="semibold">
                    {analytics.confidence}
                  </Text>
                </div>
                <ProgressBar aria-label={`Confidence level: ${analytics.confidence}`} color="success" size="panel" value={confidencePercentage} />
              </div>
            ) : (
              <div className={styles.statusItem}>
                <Text className={styles.statusLabel} color="secondary" size="sm">
                  Current
                </Text>
                <Text className={styles.statusValue} size="sm" weight="semibold">
                  N/A
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Section - Activity Summary */}
        <div className={styles.bottomSection}>
          <div className={styles.sectionHeader}>
            <Text color="secondary" size="xs" weight="medium">
              ACTIVITY SUMMARY
            </Text>
          </div>

          <div className={styles.activityGrid}>
            <div className={styles.activityItem}>
              <Text className={styles.activityLabel} color="secondary" size="sm">
                Reports
              </Text>
              <Text className={styles.activityValue} size="base" weight="semibold">
                {analytics.totalReports || 'N/A'}
              </Text>
            </div>

            <div className={styles.activityItem}>
              <Text className={styles.activityLabel} color="secondary" size="sm">
                Sessions
              </Text>
              <Text className={styles.activityValue} size="base" weight="semibold">
                {activeSessionsCount}/{analytics.auditSessions || 0}
              </Text>
            </div>

            <div className={styles.activityItem}>
              <Text className={styles.activityLabel} color="secondary" size="sm">
                Items Audited
              </Text>
              <Text className={styles.activityValue} size="base" weight="semibold">
                {analytics.itemsAudited ? analytics.itemsAudited.toLocaleString() : 'N/A'}
              </Text>
            </div>
          </div>
        </div>
      </BaseItem>
    </div>
  );
}
